// Shared presentation helpers for the HR app.
import type { ComposerTranslation } from 'vue-i18n'

const EMPLOYEE_TYPE_KEYS: Record<string, string> = {
  office: 'hr.employeeType.office',
  production: 'hr.employeeType.production',
  mixed: 'hr.employeeType.mixed',
}

const BILLING_MODE_KEYS: Record<string, string> = {
  hourly: 'hr.billingMode.hourly',
  production_cycle: 'hr.billingMode.production_cycle',
  fixed_salary: 'hr.billingMode.fixed_salary',
  fixed_task: 'hr.billingMode.fixed_task',
}

const ATTENDANCE_STATUS_KEYS: Record<string, string> = {
  present: 'hr.attendanceStatus.present',
  absent: 'hr.attendanceStatus.absent',
  late: 'hr.attendanceStatus.late',
  leave: 'hr.attendanceStatus.leave',
  remote: 'hr.attendanceStatus.remote',
  half_day: 'hr.attendanceStatus.half_day',
}

const ENTRY_TYPE_KEYS: Record<string, string> = {
  office_task: 'hr.entryType.office_task',
  production_cycle: 'hr.entryType.production_cycle',
  admin_task: 'hr.entryType.admin_task',
}

const BILLING_UNIT_KEYS: Record<string, string> = {
  hour: 'hr.billingUnit.hour',
  cycle: 'hr.billingUnit.cycle',
  fixed: 'hr.billingUnit.fixed',
}

function resolve(t: ComposerTranslation, keys: Record<string, string>, value: string | null | undefined): string {
  if (!value) return '—'
  const key = keys[value]
  if (!key) return value
  const label = t(key)
  return label && !String(label).startsWith('hr.') ? label : value
}

export function employeeTypeLabel(t: ComposerTranslation, value: string | null | undefined): string {
  return resolve(t, EMPLOYEE_TYPE_KEYS, value)
}

export function billingModeLabel(t: ComposerTranslation, value: string | null | undefined): string {
  return resolve(t, BILLING_MODE_KEYS, value)
}

export function attendanceStatusLabel(t: ComposerTranslation, value: string | null | undefined): string {
  return resolve(t, ATTENDANCE_STATUS_KEYS, value)
}

export function entryTypeLabel(t: ComposerTranslation, value: string | null | undefined): string {
  return resolve(t, ENTRY_TYPE_KEYS, value)
}

export function billingUnitLabel(t: ComposerTranslation, value: string | null | undefined): string {
  return resolve(t, BILLING_UNIT_KEYS, value)
}

/** CSS class used for an attendance status badge. */
export function attendanceStatusClass(status: string | null | undefined): string {
  switch (status) {
    case 'present':
      return 'status-ok'
    case 'late':
    case 'half_day':
      return 'status-warn'
    case 'absent':
      return 'status-critical'
    case 'leave':
      return 'status-info'
    default:
      return 'status-neutral'
  }
}

/** Format a decimal number with thousand separators. */
export function formatNumber(value: number | string | null | undefined, digits = 2): string {
  const number = Number(value ?? 0)
  if (!Number.isFinite(number)) return '0'
  return number.toLocaleString(undefined, { maximumFractionDigits: digits })
}

/** Format worked minutes as `H:MM` hours. */
export function formatMinutesAsHours(minutes: number | string | null | undefined): string {
  const total = Math.max(0, Math.round(Number(minutes ?? 0)))
  const hours = Math.floor(total / 60)
  const rest = total % 60
  return `${hours}:${String(rest).padStart(2, '0')}`
}

/** Local `YYYY-MM-DD` for a Date. */
export function toDateInput(value: Date): string {
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${value.getFullYear()}-${month}-${day}`
}

/** First day of the current month as `YYYY-MM-DD`. */
export function monthStart(reference = new Date()): string {
  return toDateInput(new Date(reference.getFullYear(), reference.getMonth(), 1))
}

/** Last day of the current month as `YYYY-MM-DD`. */
export function monthEnd(reference = new Date()): string {
  return toDateInput(new Date(reference.getFullYear(), reference.getMonth() + 1, 0))
}

/** `YYYY-MM` value for a month input from a `YYYY-MM-DD` string. */
export function monthValue(date: string): string {
  return (date || '').slice(0, 7)
}

/** Every date between two `YYYY-MM-DD` values (inclusive). */
export function dateRange(from: string, to: string, maxDays = 62): string[] {
  const dates: string[] = []
  if (!from || !to || from > to) return dates
  const cursor = new Date(`${from}T00:00:00`)
  const end = new Date(`${to}T00:00:00`)
  while (cursor <= end && dates.length < maxDays) {
    dates.push(toDateInput(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return dates
}
